import React, { useState, useEffect, useRef, useMemo } from 'react';
import { User, FileText, AlertTriangle, Camera, Video, Link as LinkIcon, Plus, Trash2, Calendar, ExternalLink, Edit2, Check, X, Image, Film, Shield, RefreshCw, Clock, ChevronDown, ChevronUp, Target, Download, MessageSquare, Send, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
};

const PlayerPortal = () => {
  const [profile, setProfile] = useState(null);
  const [notes, setNotes] = useState([]);
  const [injuries, setInjuries] = useState([]);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [newNote, setNewNote] = useState('');
  const [newInjury, setNewInjury] = useState({ description: '', injury_date: '', expected_return: '', status: 'active' });
  const [showInjuryForm, setShowInjuryForm] = useState(false);
  const [newMediaLink, setNewMediaLink] = useState({ name: '', url: '', media_type: 'clip_link' });
  const [showMediaLinkForm, setShowMediaLinkForm] = useState(false);
  const [clubContacts, setClubContacts] = useState([]);
  const [expandedHistory, setExpandedHistory] = useState(null);
  const [renegotiations, setRenegotiations] = useState([]);
  const mediaInputRef = useRef(null);
  const [portalMessages, setPortalMessages] = useState([]);
  const [expandedMessage, setExpandedMessage] = useState(null);
  const [msgReplyText, setMsgReplyText] = useState('');
  const [passwordForm, setPasswordForm] = useState({ old_password: '', new_password: '', confirm_password: '' });
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [profileRes, notesRes, injuriesRes, mediaRes, messagesRes] = await Promise.all([
        api.get('/player-portal/profile'),
        api.get('/player-portal/notes'),
        api.get('/player-portal/injuries'),
        api.get('/player-portal/media'),
        api.get('/messages')
      ]);
      setProfile(profileRes.data);
      setNotes(notesRes.data);
      setInjuries(injuriesRes.data);
      setMedia(mediaRes.data);
      setPortalMessages(messagesRes.data);
      // Fetch club contacts and renegotiations after profile loads
      if (profileRes.data?.id) {
        try {
          const [contactsRes, regoRes] = await Promise.all([
            api.get(`/player-club-contacts/${profileRes.data.id}`),
            api.get(`/contract-renegotiations/${profileRes.data.id}`)
          ]);
          setClubContacts(contactsRes.data);
          setRenegotiations(regoRes.data);
        } catch (error) { console.error('Failed to load contacts/renegotiations:', error); }
      }
    } catch (error) {
      toast.error('Failed to load portal data');
    } finally {
      setLoading(false);
    }
  };

  const submitNote = async () => {
    if (!newNote.trim()) return;
    try {
      const res = await api.post('/player-portal/notes', {
        player_id: profile.id,
        content: newNote
      });
      setNotes([res.data, ...notes]);
      setNewNote('');
      toast.success('Note added');
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const submitInjury = async () => {
    if (!newInjury.description.trim()) return;
    try {
      const res = await api.post('/player-portal/injuries', {
        player_id: profile.id,
        ...newInjury
      });
      setInjuries([res.data, ...injuries]);
      setNewInjury({ description: '', injury_date: '', expected_return: '', status: 'active' });
      setShowInjuryForm(false);
      toast.success('Injury report submitted');
    } catch (error) {
      toast.error('Failed to submit injury report');
    }
  };

  const submitMediaLink = async () => {
    if (!newMediaLink.url.trim()) return;
    try {
      const res = await api.post('/player-portal/media', {
        player_id: profile.id,
        ...newMediaLink
      });
      setMedia([res.data, ...media]);
      setNewMediaLink({ name: '', url: '', media_type: 'clip_link' });
      setShowMediaLinkForm(false);
      toast.success('Link added');
    } catch (error) {
      toast.error('Failed to add link');
    }
  };

  const handleMediaUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    if (!isImage && !isVideo) { toast.error('Please upload an image or video file'); return; }
    if (file.size > 50 * 1024 * 1024) { toast.error('File must be less than 50MB'); return; }

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const res = await api.post('/player-portal/media', {
          player_id: profile.id,
          media_type: isImage ? 'photo' : 'video',
          name: file.name,
          data: event.target.result
        });
        setMedia([res.data, ...media]);
        toast.success(`${isImage ? 'Photo' : 'Video'} uploaded`);
      } catch (error) {
        toast.error('Upload failed');
      }
    };
    reader.readAsDataURL(file);
    if (mediaInputRef.current) mediaInputRef.current.value = '';
  };

  const deleteMedia = async (mediaId) => {
    try {
      await api.delete(`/player-portal/media/${mediaId}`);
      setMedia(media.filter(m => m.id !== mediaId));
      toast.success('Deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const sendPortalReply = async (msgId) => {
    if (!msgReplyText.trim()) return;
    try {
      const res = await api.post(`/messages/${msgId}/reply`, { body: msgReplyText });
      setPortalMessages(portalMessages.map(m => m.id === msgId ? { ...m, replies: [...(m.replies || []), res.data] } : m));
      setMsgReplyText('');
      toast.success('Reply sent');
    } catch { toast.error('Failed to send reply'); }
  };

  const formatMsgDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const changePassword = async () => {
    if (!passwordForm.old_password || !passwordForm.new_password) { toast.error('Please fill in both fields'); return; }
    if (passwordForm.new_password !== passwordForm.confirm_password) { toast.error('New passwords do not match'); return; }
    if (passwordForm.new_password.length < 4) { toast.error('Password must be at least 4 characters'); return; }
    try {
      await api.post('/player-portal/change-password', { old_password: passwordForm.old_password, new_password: passwordForm.new_password });
      toast.success('Password changed successfully');
      setPasswordForm({ old_password: '', new_password: '', confirm_password: '' });
      setShowPasswordForm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090B]">
        <div className="text-white">Loading your portal...</div>
      </div>
    );
  }

  const tabs = [
    { id: 'profile', label: 'My Profile', icon: User },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
    { id: 'career', label: 'Career Path', icon: Target },
    { id: 'clubs', label: 'Clubs', icon: Shield },
    { id: 'contract', label: 'Contract', icon: RefreshCw },
    { id: 'links', label: 'Links', icon: LinkIcon },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'notes', label: 'Notes', icon: FileText },
    { id: 'injuries', label: 'Injuries', icon: AlertTriangle },
    { id: 'media', label: 'Media', icon: Camera },
    { id: 'booking', label: 'Book Meeting', icon: Calendar },
  ];

  return (
    <div className="min-h-screen bg-[#09090B] noise-texture">
      {/* Header */}
      <div className="relative bg-black/60 backdrop-blur-xl border-b border-white/5 overflow-hidden">
        <div className="hero-glow"></div>
        <div className="relative z-10 p-6 md:p-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">DC Sports & Talent</p>
              <p className="text-[9px] uppercase tracking-[0.2em] text-zinc-600">Player First. Opportunity Always.</p>
            </div>
            <button
              onClick={() => { localStorage.removeItem('token'); localStorage.removeItem('user'); window.location.href = '/login'; }}
              data-testid="portal-logout-button"
              className="px-4 py-2 bg-white/10 text-white hover:bg-white/20 rounded-sm text-sm font-bold uppercase tracking-wide transition-all"
            >
              Logout
            </button>
          </div>
          <div className="flex items-center gap-4">
            {profile?.photo ? (
              <img src={profile.photo} alt={profile?.name} className="w-14 h-14 rounded-full object-cover border-2 border-white/20" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-zinc-800 border-2 border-white/10 flex items-center justify-center">
                <User className="w-7 h-7 text-zinc-500" />
              </div>
            )}
            <div>
              <h1 data-testid="portal-player-name" className="text-2xl md:text-3xl font-bold uppercase tracking-tight text-white">
                {profile?.name || 'Player Portal'}
              </h1>
              <p className="text-sm text-zinc-400">
                {profile?.position}{profile?.position2 ? ` / ${profile.position2}` : ''} {profile?.current_club ? `• ${profile.current_club}` : ''}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-white/5 bg-black/30">
        <div className="flex overflow-x-auto p-2 gap-1 scrollbar-hide">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`portal-tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-sm text-sm font-bold uppercase tracking-wide whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-white text-black'
                    : 'text-zinc-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="p-6 md:p-8 max-w-5xl mx-auto">
        {/* PROFILE TAB */}
        {activeTab === 'profile' && profile && (
          <div className="space-y-6 animate-fade-in">
            <div className="glass-card p-6">
              <h2 className="text-xl font-bold uppercase tracking-tight text-white mb-4">Personal Details</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  ['Name', profile.name],
                  ['Position', `${profile.position}${profile.position2 ? ` / ${profile.position2}` : ''}`],
                  ['Age', profile.age],
                  ['Nationality', profile.nationality],
                  ['Date of Birth', formatDate(profile.date_of_birth)],
                  ['Current Club', profile.current_club],
                  ['Preferred Foot', profile.preferred_foot],
                  ['Player Type', profile.player_type],
                ].filter(([, val]) => val).map(([label, value]) => (
                  <div key={label}>
                    <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">{label}</p>
                    <p className="text-white font-medium">{value}</p>
                  </div>
                ))}
              </div>
            </div>
            {profile.contract_end_date && (
              <div className="glass-card p-6">
                <h2 className="text-xl font-bold uppercase tracking-tight text-white mb-4">Contract</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Contract End</p>
                    <p className="text-white font-medium">{formatDate(profile.contract_end_date)}</p>
                  </div>
                  {profile.salary && (
                    <div>
                      <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Salary</p>
                      <p className="text-green-400 font-bold">£{profile.salary.toLocaleString()}/{profile.salary_frequency || 'year'}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Change Password - shown at bottom of profile */}
        {activeTab === 'profile' && (
          <div className="mt-6">
            {!showPasswordForm ? (
              <button onClick={() => setShowPasswordForm(true)} data-testid="show-change-password-btn"
                className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 hover:bg-white/10 text-zinc-400 hover:text-white rounded-md text-sm transition-all">
                <KeyRound className="w-4 h-4" /> Change Password
              </button>
            ) : (
              <div className="glass-card p-5 space-y-3" data-testid="change-password-form">
                <h3 className="text-sm font-bold uppercase tracking-widest text-zinc-400 flex items-center gap-2">
                  <KeyRound className="w-4 h-4" /> Change Password
                </h3>
                <input type="password" value={passwordForm.old_password} onChange={(e) => setPasswordForm({ ...passwordForm, old_password: e.target.value })}
                  data-testid="old-password-input" placeholder="Current password"
                  className="w-full bg-zinc-900 border border-white/10 rounded-md px-3 py-2 text-white text-sm placeholder:text-zinc-600 outline-none max-w-sm" />
                <input type="password" value={passwordForm.new_password} onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  data-testid="new-password-input" placeholder="New password"
                  className="w-full bg-zinc-900 border border-white/10 rounded-md px-3 py-2 text-white text-sm placeholder:text-zinc-600 outline-none max-w-sm" />
                <input type="password" value={passwordForm.confirm_password} onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                  data-testid="confirm-password-input" placeholder="Confirm new password"
                  className="w-full bg-zinc-900 border border-white/10 rounded-md px-3 py-2 text-white text-sm placeholder:text-zinc-600 outline-none max-w-sm" />
                <div className="flex gap-2">
                  <button onClick={changePassword} data-testid="save-password-btn"
                    className="px-4 py-2 bg-white text-black hover:bg-zinc-200 font-bold uppercase text-xs rounded-sm transition-all">Save</button>
                  <button onClick={() => { setShowPasswordForm(false); setPasswordForm({ old_password: '', new_password: '', confirm_password: '' }); }}
                    className="px-4 py-2 bg-white/10 text-white hover:bg-white/20 text-xs rounded-sm transition-all">Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* MESSAGES TAB */}
        {activeTab === 'messages' && (
          <div className="space-y-6 animate-fade-in">
            <h2 className="text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-white" />
              Messages
            </h2>
            {portalMessages.length > 0 ? (
              <div className="space-y-3">
                {portalMessages.map((msg) => (
                  <div key={msg.id} className="glass-card overflow-hidden" data-testid={`portal-msg-${msg.id}`}>
                    <div className="p-4 cursor-pointer hover:bg-white/5 transition-colors"
                      onClick={() => { setExpandedMessage(expandedMessage === msg.id ? null : msg.id); if (expandedMessage !== msg.id) { api.post(`/messages/${msg.id}/read`).catch(() => {}); } }}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-bold text-sm">{msg.subject}</h3>
                          <p className="text-zinc-500 text-xs mt-1">From {msg.sender_name} · {formatMsgDate(msg.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                          {(msg.replies?.length > 0) && (
                            <span className="bg-white/20 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                              {msg.replies.length}
                            </span>
                          )}
                          {expandedMessage === msg.id ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
                        </div>
                      </div>
                    </div>
                    {expandedMessage === msg.id && (
                      <div className="border-t border-white/10 p-4 bg-white/[0.02] space-y-4">
                        <p className="text-zinc-300 text-sm whitespace-pre-wrap">{msg.body}</p>
                        {msg.replies?.length > 0 && (
                          <div className="space-y-2">
                            {msg.replies.map((r) => (
                              <div key={r.id} className={`p-3 rounded-lg text-sm ${r.sender_role === 'admin' ? 'bg-white/10 border border-[#FFFFFF]/20' : 'bg-white/5 border border-white/10'}`}>
                                <p className="text-xs text-zinc-500 mb-1"><span className="font-bold">{r.sender_name}</span> · {formatMsgDate(r.created_at)}</p>
                                <p className="text-zinc-300 whitespace-pre-wrap">{r.body}</p>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="flex gap-2">
                          <input value={msgReplyText} onChange={(e) => setMsgReplyText(e.target.value)} data-testid={`portal-reply-input-${msg.id}`}
                            placeholder="Type your reply..." onKeyDown={(e) => e.key === 'Enter' && sendPortalReply(msg.id)}
                            className="flex-1 bg-zinc-900 border border-white/10 rounded-md px-3 py-2 text-white text-sm placeholder:text-zinc-600 outline-none" />
                          <button onClick={() => sendPortalReply(msg.id)} data-testid={`portal-reply-btn-${msg.id}`}
                            className="px-4 py-2 bg-white text-black hover:bg-zinc-200 font-bold uppercase text-xs rounded-sm transition-all flex items-center gap-1">
                            <Send className="w-3 h-3" /> Reply
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="glass-card p-8 text-center text-zinc-500">No messages yet.</div>
            )}
          </div>
        )}

        {/* CAREER PATH TAB */}
        {activeTab === 'career' && (
          <div className="space-y-6 animate-fade-in">
            <h2 className="text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
              <Target className="w-5 h-5 text-green-400" />
              Career Path
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="glass-card p-6" data-testid="portal-career-1yr">
                <h3 className="text-xs font-bold uppercase tracking-widest text-green-400 mb-4">1-Year Goal</h3>
                <p className="text-zinc-300 text-sm whitespace-pre-wrap leading-relaxed">
                  {profile?.career_path_1yr || <span className="text-zinc-600 italic">No 1-year career path has been set yet.</span>}
                </p>
              </div>
              <div className="glass-card p-6" data-testid="portal-career-2yr">
                <h3 className="text-xs font-bold uppercase tracking-widest text-yellow-400 mb-4">2-Year Goal</h3>
                <p className="text-zinc-300 text-sm whitespace-pre-wrap leading-relaxed">
                  {profile?.career_path_2yr || <span className="text-zinc-600 italic">No 2-year career path has been set yet.</span>}
                </p>
              </div>
              <div className="glass-card p-6" data-testid="portal-career-3yr">
                <h3 className="text-xs font-bold uppercase tracking-widest text-blue-400 mb-4">3-Year Goal</h3>
                <p className="text-zinc-300 text-sm whitespace-pre-wrap leading-relaxed">
                  {profile?.career_path_3yr || <span className="text-zinc-600 italic">No 3-year career path has been set yet.</span>}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* CLUBS CONTACTED TAB */}
        {activeTab === 'clubs' && (
          <div className="space-y-6 animate-fade-in">
            <h2 className="text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-400" />
              Clubs Contacted
            </h2>
            {clubContacts.length === 0 ? (
              <div className="glass-card p-8 text-center text-zinc-500">No clubs have been contacted yet.</div>
            ) : (
              <div className="space-y-3">
                {clubContacts.map((contact) => {
                  const outcomeColors = {
                    yes: 'bg-green-500/20 text-green-400',
                    no: 'bg-red-500/20 text-red-400',
                    potential: 'bg-yellow-500/20 text-yellow-400',
                    trials: 'bg-blue-500/20 text-blue-400'
                  };
                  const history = contact.notes_history || [];
                  const isExpanded = expandedHistory === contact.id;
                  return (
                    <div key={contact.id} className="glass-card p-4">
                      <div className="flex items-center gap-4">
                        <Shield className="w-5 h-5 text-blue-400 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-white font-bold">{contact.club_name}</p>
                          {contact.contact_date && <p className="text-xs text-zinc-400">Contacted: {formatDate(contact.contact_date)}</p>}
                        </div>
                        <span className={`px-3 py-1 rounded text-sm font-bold uppercase ${outcomeColors[contact.outcome] || outcomeColors.potential}`}>
                          {contact.outcome}
                        </span>
                      </div>
                      {/* Notes */}
                      {contact.notes && (
                        <div className="mt-3 ml-9">
                          <p className="text-sm text-zinc-300">{contact.notes}</p>
                        </div>
                      )}
                      {/* Notes change history */}
                      {history.length > 0 && (
                        <div className="mt-2 ml-9">
                          <button
                            onClick={() => setExpandedHistory(isExpanded ? null : contact.id)}
                            data-testid={`portal-toggle-history-${contact.id}`}
                            className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300 transition-colors"
                          >
                            <Clock className="w-3 h-3" />
                            {history.length} note update{history.length !== 1 ? 's' : ''}
                            {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          </button>
                          {isExpanded && (
                            <div className="mt-2 space-y-1.5 border-l-2 border-zinc-700 pl-3">
                              {[...history].reverse().map((h, i) => (
                                <div key={i} className="text-[11px]" data-testid={`portal-note-history-${contact.id}-${i}`}>
                                  <div className="flex items-center gap-2 text-zinc-500">
                                    <span className="font-medium text-zinc-400">{h.changed_by}</span>
                                    <span>{formatDate(h.changed_at)}</span>
                                  </div>
                                  <p className="text-zinc-500 line-through">{h.previous_notes || '(empty)'}</p>
                                  <p className="text-zinc-300">{h.new_notes || '(cleared)'}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* CONTRACT RENEGOTIATION TAB */}
        {activeTab === 'contract' && (
          <div className="space-y-6 animate-fade-in">
            <h2 className="text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-orange-400" />
              Renegotiate Club Contract
            </h2>
            {renegotiations.length === 0 ? (
              <div className="glass-card p-8 text-center text-zinc-500">No contract renegotiations in progress.</div>
            ) : (
              <div className="space-y-4">
                {renegotiations.map((rego) => {
                  const statusColors = {
                    start_conversation: 'bg-blue-500/20 text-blue-400',
                    in_progress: 'bg-yellow-500/20 text-yellow-400',
                    agreed: 'bg-green-500/20 text-green-400',
                    not_accepted: 'bg-red-500/20 text-red-400'
                  };
                  const statusLabels = { start_conversation: 'Start Conversation', in_progress: 'In Progress', agreed: 'Agreed', not_accepted: 'Not Accepted' };
                  const history = rego.history || [];
                  return (
                    <div key={rego.id} className="glass-card overflow-hidden">
                      <div className="p-4 flex items-center gap-4">
                        <RefreshCw className="w-5 h-5 text-orange-400 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-white font-bold">{rego.club_name}</p>
                          {rego.notes && <p className="text-xs text-zinc-400 mt-1">{rego.notes}</p>}
                        </div>
                        <span className={`px-3 py-1 rounded text-sm font-bold uppercase ${statusColors[rego.status] || statusColors.start_conversation}`}>
                          {statusLabels[rego.status] || rego.status}
                        </span>
                      </div>
                      {history.length > 0 && (
                        <div className="px-4 pb-4 pt-1 border-t border-white/5">
                          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Timeline</p>
                          <div className="relative pl-4">
                            <div className="absolute left-[7px] top-1 bottom-1 w-px bg-white/10"></div>
                            {history.map((h, i) => {
                              const dotColor = { start_conversation: 'bg-blue-400', in_progress: 'bg-yellow-400', agreed: 'bg-green-400', not_accepted: 'bg-red-400' };
                              return (
                                <div key={i} className="relative flex items-start gap-3 pb-2 last:pb-0">
                                  <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 -ml-[3px] ${dotColor[h.status] || 'bg-zinc-400'}`}></div>
                                  <div className="flex-1 min-w-0">
                                    <span className="text-xs text-white font-medium">{h.label || statusLabels[h.status] || h.status}</span>
                                    <span className="text-[10px] text-zinc-500 ml-2">{h.changed_by} &bull; {h.timestamp ? formatDate(h.timestamp) : ''}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* LINKS TAB */}
        {activeTab === 'links' && (
          <div className="space-y-6 animate-fade-in">
            <h2 className="text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
              <LinkIcon className="w-5 h-5 text-blue-400" />
              Links
            </h2>
            {profile?.links?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {profile.links.map((link, i) => (
                  <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                    className="glass-card p-4 flex items-center gap-3 hover:bg-white/10 transition-colors group" data-testid={`portal-link-${i}`}>
                    <ExternalLink className="w-5 h-5 text-white flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-white font-medium truncate group-hover:text-white transition-colors">{link.name || link.url}</p>
                      <p className="text-zinc-500 text-xs truncate">{link.url}</p>
                    </div>
                  </a>
                ))}
              </div>
            ) : (
              <div className="glass-card p-8 text-center text-zinc-500">No links have been added to your profile yet.</div>
            )}
          </div>
        )}

        {/* DOCUMENTS TAB */}
        {activeTab === 'documents' && (
          <div className="space-y-6 animate-fade-in">
            <h2 className="text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-400" />
              Documents
            </h2>
            {profile?.documents?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {profile.documents.map((doc, i) => (
                  <a key={i} href={doc.url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${doc.url}` : doc.url} target="_blank" rel="noopener noreferrer"
                    className="glass-card p-4 flex items-center gap-3 hover:bg-white/10 transition-colors group" data-testid={`portal-doc-${i}`}>
                    <Download className="w-5 h-5 text-orange-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-white font-medium truncate group-hover:text-white transition-colors">{doc.name || 'Document'}</p>
                      <p className="text-zinc-500 text-xs">PDF Document</p>
                    </div>
                  </a>
                ))}
              </div>
            ) : (
              <div className="glass-card p-8 text-center text-zinc-500">No documents have been uploaded to your profile yet.</div>
            )}
          </div>
        )}

        {/* NOTES TAB */}
        {activeTab === 'notes' && (
          <div className="space-y-6 animate-fade-in">
            <div className="glass-card p-6">
              <h2 className="text-xl font-bold uppercase tracking-tight text-white mb-4">Add Note</h2>
              <textarea
                rows="3"
                data-testid="portal-note-input"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none mb-3"
                placeholder="Write a note..."
              />
              <button
                onClick={submitNote}
                data-testid="portal-submit-note"
                className="px-6 py-2 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm transition-all"
              >
                Add Note
              </button>
            </div>
            {notes.length === 0 ? (
              <div className="glass-card p-8 text-center text-zinc-500">No notes yet</div>
            ) : (
              <div className="space-y-3">
                {notes.map((note) => (
                  <div key={note.id} className="glass-card p-4">
                    <p className="text-white mb-2">{note.content}</p>
                    <div className="flex justify-between text-xs text-zinc-500">
                      <span>{note.created_by} ({note.created_by_role})</span>
                      <span>{formatDate(note.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* INJURIES TAB */}
        {activeTab === 'injuries' && (
          <div className="space-y-6 animate-fade-in">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold uppercase tracking-tight text-white">Injury Tracker</h2>
              <button
                onClick={() => setShowInjuryForm(!showInjuryForm)}
                data-testid="portal-add-injury-button"
                className="flex items-center gap-2 px-4 py-2 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm text-sm transition-all"
              >
                <Plus className="w-4 h-4" />
                Report Injury
              </button>
            </div>
            {showInjuryForm && (
              <div className="glass-card p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Description *</label>
                    <input
                      type="text"
                      data-testid="portal-injury-desc"
                      value={newInjury.description}
                      onChange={(e) => setNewInjury({ ...newInjury, description: e.target.value })}
                      className="w-full bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none"
                      placeholder="e.g., Hamstring strain - left leg"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Injury Date</label>
                    <input type="date" value={newInjury.injury_date} onChange={(e) => setNewInjury({ ...newInjury, injury_date: e.target.value })}
                      className="w-full bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-3 text-white outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Expected Return</label>
                    <input type="date" value={newInjury.expected_return} onChange={(e) => setNewInjury({ ...newInjury, expected_return: e.target.value })}
                      className="w-full bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-3 text-white outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Status</label>
                    <select value={newInjury.status} onChange={(e) => setNewInjury({ ...newInjury, status: e.target.value })}
                      className="w-full bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-3 text-white outline-none"
                    >
                      <option value="active">Active</option>
                      <option value="recovering">Recovering</option>
                      <option value="resolved">Resolved</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={submitInjury} data-testid="portal-submit-injury"
                    className="px-6 py-2 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm transition-all"
                  >
                    Submit
                  </button>
                  <button onClick={() => setShowInjuryForm(false)} className="px-4 py-2 bg-white/10 text-white hover:bg-white/20 rounded-sm transition-all">Cancel</button>
                </div>
              </div>
            )}
            {injuries.length === 0 ? (
              <div className="glass-card p-8 text-center text-zinc-500">No injuries reported</div>
            ) : (
              <div className="space-y-3">
                {injuries.map((injury) => (
                  <div key={injury.id} className={`glass-card p-4 border-l-4 ${
                    injury.status === 'active' ? 'border-l-red-500' : injury.status === 'recovering' ? 'border-l-yellow-500' : 'border-l-green-500'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-white font-bold mb-1">{injury.description}</p>
                        <div className="flex gap-4 text-xs text-zinc-400">
                          {injury.injury_date && <span>Injured: {formatDate(injury.injury_date)}</span>}
                          {injury.expected_return && <span>Return: {formatDate(injury.expected_return)}</span>}
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs font-bold uppercase rounded ${
                        injury.status === 'active' ? 'bg-red-500/20 text-red-400' : injury.status === 'recovering' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-green-500/20 text-green-400'
                      }`}>
                        {injury.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* MEDIA TAB */}
        {activeTab === 'media' && (
          <div className="space-y-6 animate-fade-in">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="text-xl font-bold uppercase tracking-tight text-white">Photos, Clips & Videos</h2>
              <div className="flex gap-2">
                <input type="file" ref={mediaInputRef} accept="image/*,video/*" onChange={handleMediaUpload} className="hidden" id="media-upload" />
                <label htmlFor="media-upload" data-testid="portal-upload-media"
                  className="flex items-center gap-2 px-4 py-2 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm text-sm transition-all cursor-pointer"
                >
                  <Camera className="w-4 h-4" />
                  Upload
                </label>
                <button onClick={() => setShowMediaLinkForm(!showMediaLinkForm)} data-testid="portal-add-clip-link"
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 font-bold uppercase tracking-wide rounded-sm text-sm transition-all"
                >
                  <LinkIcon className="w-4 h-4" />
                  Add Link
                </button>
              </div>
            </div>

            {showMediaLinkForm && (
              <div className="glass-card p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <input type="text" placeholder="Name (e.g., Match Highlights)" value={newMediaLink.name}
                    onChange={(e) => setNewMediaLink({ ...newMediaLink, name: e.target.value })}
                    className="bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none"
                  />
                  <input type="url" placeholder="URL (YouTube, Instagram, etc.)" value={newMediaLink.url}
                    onChange={(e) => setNewMediaLink({ ...newMediaLink, url: e.target.value })}
                    className="bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none"
                  />
                  <div className="flex gap-2">
                    <button onClick={submitMediaLink} className="flex-1 px-4 py-2 bg-white text-black hover:bg-zinc-200 font-bold rounded-sm">Save</button>
                    <button onClick={() => setShowMediaLinkForm(false)} className="px-4 py-2 bg-white/10 text-white hover:bg-white/20 rounded-sm">Cancel</button>
                  </div>
                </div>
              </div>
            )}

            {media.length === 0 ? (
              <div className="glass-card p-8 text-center text-zinc-500">No media yet. Upload photos, videos, or add clip links!</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {media.map((item) => (
                  <div key={item.id} className="glass-card p-4 group">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2 min-w-0">
                        {item.media_type === 'photo' ? <Image className="w-4 h-4 text-green-400 flex-shrink-0" /> :
                         item.media_type === 'video' ? <Film className="w-4 h-4 text-purple-400 flex-shrink-0" /> :
                         <LinkIcon className="w-4 h-4 text-blue-400 flex-shrink-0" />}
                        <span className="text-white font-medium text-sm truncate">{item.name}</span>
                      </div>
                      <button onClick={() => deleteMedia(item.id)}
                        className="p-1 hover:bg-red-500/20 rounded text-zinc-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {item.media_type === 'photo' && item.data && (
                      <img src={item.data} alt={item.name} className="w-full h-40 object-cover rounded-lg mb-2" />
                    )}
                    {item.media_type === 'video' && item.data && (
                      <video src={item.data} controls className="w-full h-40 rounded-lg mb-2 bg-black" />
                    )}
                    {item.media_type === 'clip_link' && item.url && (
                      <a href={item.url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 truncate"
                      >
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                        {item.url}
                      </a>
                    )}
                    <p className="text-xs text-zinc-500 mt-2">{formatDate(item.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* BOOKING TAB */}
        {activeTab === 'booking' && (
          <div className="space-y-6 animate-fade-in">
            <div className="glass-card p-6 text-center">
              <Calendar className="w-12 h-12 text-white mx-auto mb-4" />
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-2">Book a Meeting</h2>
              <p className="text-zinc-400 mb-6">Schedule a meeting with Danny at DC Sports & Talent</p>
              <a
                href="https://calendly.com/danny-dcsportsandtalent"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="portal-calendly-link"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm text-lg transition-all"
              >
                <Calendar className="w-5 h-5" />
                Open Calendly
              </a>
              <p className="text-xs text-zinc-500 mt-4">You'll be redirected to Calendly to choose a time slot</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerPortal;
